import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Lock, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export const money = (c) => `$${(c / 100).toFixed(2).replace(/\.00$/, "")}`;
export const money2 = (c) => `$${(c / 100).toFixed(2)}`;
export const fday = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");
const nice = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString([], { month: "short", day: "numeric" });

// Plan / meter errors from the API → friendly copy. Kids never see plan names or prices (REQ-3516/3551).
export function planErr(e, { kid = false, what = "Copilot" } = {}) {
  const d = e?.response?.data?.detail;
  if (!d || typeof d !== "object") return null;
  if (d.code === "fair_use_limit") return kid ? `The ${what === "stories" ? "story maker" : "Copilot"} is resting until ${nice(d.resets_on)}. Ask a grown-up.` : d.message;
  if (d.code === "plan_required" || d.code === "plan_limit") return kid ? "Ask a grown-up about this." : d.message;
  return null;
}

let cache = null;
export function usePublicPlans() {
  const [d, setD] = useState(cache);
  useEffect(() => { api.get("/plans").then(({ data }) => { cache = data; setD(data); }).catch(() => {}); }, []);
  return d;
}

const planInfo = {};
export function usePlan(hubId) {
  const [d, setD] = useState(planInfo[hubId] || null);
  useEffect(() => { api.get(`/organizations/${hubId}/plan`).then(({ data }) => { planInfo[hubId] = data; setD(data); }).catch(() => {}); }, [hubId]);
  return d;
}

export function FoundingBanner({ plans, testid = "founding-banner" }) {
  const f = plans?.founding;
  if (!f?.show) return null;
  return (
    <div className="flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold text-q-text" style={{ borderColor: "rgba(var(--glow-family),0.45)", background: "rgba(var(--glow-family),0.1)" }} data-testid={testid}>
      <Sparkles className="h-4 w-4 shrink-0 text-[rgb(var(--glow-family))]" />
      <span>Founding families: <b data-testid={`${testid}-left`}>{f.left} of {f.cap}</b> spots left · Family Plus for {money2(f.price_cents)}/mo for {f.months} months, plus a {f.trial_days}-day free trial</span>
    </div>
  );
}

export function IntervalToggle({ value, onChange }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-q-line p-1" role="radiogroup" data-testid="plan-interval-toggle">
      {[["month", "Monthly"], ["year", "Yearly"]].map(([v, l]) => (
        <button key={v} role="radio" aria-checked={value === v} onClick={() => onChange(v)} data-testid={`plan-interval-${v}`}
          className={`min-h-[40px] rounded-full px-4 text-sm font-bold transition-colors ${value === v ? "bg-[rgb(var(--glow-family))] text-white" : "text-q-muted"}`}>{l}</button>
      ))}
      <span className="px-2 text-xs font-bold text-[rgb(var(--glow-money))]">Yearly saves about 2 months</span>
    </div>
  );
}

export function PlanCards({ plans, interval, current, onPick, busy, cta = "Choose" }) {
  if (!plans) return null;
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="plan-cards">
      {plans.plans.map((p) => {
        const mine = current === p.key;
        return (
          <div key={p.key} className={`relative flex flex-col rounded-3xl border bg-q-surface p-5 transition-transform duration-200 hover:-translate-y-0.5 ${p.popular ? "border-[rgb(var(--glow-family))] shadow-[0_0_30px_rgba(var(--glow-family),0.18)]" : "border-q-line"}`} data-testid={`plan-card-${p.key}`}>
            {p.popular && <span className="absolute -top-3 left-5 rounded-full bg-[rgb(var(--glow-family))] px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-white" data-testid="plan-popular-badge">Most popular</span>}
            <p className="font-display text-xl font-extrabold text-q-text">{p.name}</p>
            <p className="mt-1 font-display text-3xl font-extrabold text-q-text" data-testid={`plan-price-${p.key}`}>{money2(interval === "year" ? p.year_cents : p.month_cents).replace(/\.00$/, interval === "year" ? "" : ".00")}<span className="text-sm font-semibold text-q-muted">/{interval === "year" ? "yr" : "mo"}</span></p>
            <ul className="mt-4 flex-1 space-y-2 text-sm text-q-muted">
              {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--glow-money))]" />{f}</li>)}
            </ul>
            {onPick && (mine ? <p className="mt-5 text-center text-sm font-bold text-q-text" data-testid={`plan-current-${p.key}`}>Your plan</p>
              : <button className={`q-btn mt-5 min-h-[44px] w-full ${p.popular ? "q-btn-primary" : "q-btn-secondary"}`} disabled={!!busy} onClick={() => onPick(p.key)} data-testid={`plan-pick-${p.key}`}>{cta} {p.name}</button>)}
          </div>
        );
      })}
    </div>
  );
}

export function PublicPlans({ compact = false }) {
  const plans = usePublicPlans();
  const [iv, setIv] = useState("month");
  if (!plans) return null;
  return (
    <section className="space-y-4" data-testid="public-plans">
      <FoundingBanner plans={plans} />
      {!compact && <IntervalToggle value={iv} onChange={setIv} />}
      <PlanCards plans={plans} interval={iv} />
      <p className="text-xs text-q-faint" data-testid="public-plans-note">Every hub starts with a {plans.trial_days}-day free trial on Family Plus. Roommates: {money2(plans.roommates.per_person_cents)} per roommate a month, max {money2(plans.roommates.cap_cents)}.</p>
    </section>
  );
}

// Lock card for features the hub's plan doesn't include (REQ-3521).
export function PlanLock({ hub, plan = "Family Plus", what, testid = "plan-lock" }) {
  const info = usePlan(hub.id);
  const kid = hub.my_role === "kid";
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-q-line bg-q-raised p-4" data-testid={testid}>
      <Lock className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--glow-family))]" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-q-text">{kid ? "Ask a grown-up" : `Part of ${plan}`}</p>
        {!kid && what && <p className="text-sm text-q-muted">{what}</p>}
        {!kid && (info?.is_owner || hub.my_role === "owner"
          ? <Link to={`/h/${hub.id}/family/billing`} className="q-btn q-btn-primary mt-3 min-h-[44px]" data-testid={`${testid}-see-plans`}>See plans</Link>
          : <p className="mt-1 text-sm text-q-muted" data-testid={`${testid}-ask`}>Ask {info?.owner_name || "the owner"} about changing plans.</p>)}
      </div>
    </div>
  );
}
